import { Fragment, useRef } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

export const COUPON_CODE_LENGTH = 8;
const GROUP_SIZE = COUPON_CODE_LENGTH / 2;
const boxes = Array.from({ length: COUPON_CODE_LENGTH }, (_, index) => index);

export const cleanCouponCode = (text: string) =>
  text.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, COUPON_CODE_LENGTH);

interface CouponCodeInputProps {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
  /** The server refused the code: every box gets a red border. */
  invalid?: boolean;
}

/** The code is typed or pasted into two groups of four boxes. Typing moves on, backspace moves back. */
export const CouponCodeInput = ({ value, onChange, disabled, invalid = false }: CouponCodeInputProps) => {
  const { t } = useTranslation();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const focusBox = (index: number) => inputs.current[Math.min(index, COUPON_CODE_LENGTH - 1)]?.focus();

  const type = (index: number, text: string) => {
    const char = cleanCouponCode(text).slice(-1);
    if (!char) return;
    onChange(value.slice(0, index) + char + value.slice(index + 1));
    focusBox(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace') return;
    event.preventDefault();
    if (value[index]) {
      onChange(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    if (index > 0) {
      onChange(value.slice(0, index - 1) + value.slice(index));
      focusBox(index - 1);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const code = cleanCouponCode(event.clipboardData.getData('text'));
    onChange(code);
    focusBox(code.length);
  };

  // Phones share the row between the boxes; from sm up each box has the Figma width.
  return (
    <div
      role="group"
      aria-label={t('COUPON_CODE_LABEL', 'Coupon code')}
      className="flex w-full items-center gap-1.5 sm:w-auto"
    >
      {boxes.map((index) => (
        <Fragment key={index}>
          {index === GROUP_SIZE && (
            <span aria-hidden="true" className="shrink-0 px-0.5 text-base text-grey-400 dark:text-muted-foreground">
              &ndash;
            </span>
          )}
          <Input
            ref={(element) => {
              inputs.current[index] = element;
            }}
            value={value[index] ?? ''}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={invalid}
            aria-label={t('COUPON_CHARACTER', { number: index + 1, defaultValue: 'Coupon character {{number}}' })}
            className={cn(
              'h-11 w-0 min-w-0 flex-1 rounded-xl bg-background px-0 text-center text-sm font-medium uppercase text-grey-950 shadow-none dark:text-foreground sm:w-10 sm:flex-none',
              invalid ? 'border-error-700 dark:border-error-500' : 'border-grey-100 dark:border-border'
            )}
            onChange={(event) => type(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={handlePaste}
            onFocus={(event) => event.target.select()}
          />
        </Fragment>
      ))}
    </div>
  );
};
